<template>
  <b-modal
    :visible="show"
    @hide="handleClose"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    :title="modalTitle"
    modal-class="credential-capture-modal"
  >
    <!-- Step Indicator -->
    <CaptureStepIndicator :current-step="currentStep" />

    <!-- Step Content -->
    <div class="step-content">
      <!-- Step 1: Introduction -->
      <div v-if="currentStep === 1" class="step-intro">
        <div class="intro-icon">
          <i class="ni ni-lock-circle-open"></i>
        </div>
        <h3 class="intro-title">Автоматический захват данных</h3>
        <p class="intro-description">
          Этот мастер поможет автоматически захватить учётные данные провайдера через прокси-сервер.
        </p>

        <!-- Proxy Already Running Warning -->
        <div v-if="proxyAlreadyRunning" class="proxy-warning">
          <div class="warning-icon">
            <i class="ni ni-notification-70"></i>
          </div>
          <div class="warning-content">
            <h4>Прокси уже запущен</h4>
            <p>Обнаружена активная сессия захвата. Вы можете остановить её и начать новую.</p>
            <base-button type="warning" size="sm" @click="stopExistingAndStart" :disabled="loading">
              <span v-if="loading">
                <i class="ni ni-settings-gear-65 spin mr-1"></i>
                Остановка...
              </span>
              <span v-else>
                <i class="ni ni-button-power mr-1"></i>
                Остановить и начать заново
              </span>
            </base-button>
          </div>
        </div>

        <div v-if="!proxyAlreadyRunning" class="requirements-section">
          <h4>Требования:</h4>
          <ul class="requirements-list">
            <li>
              <i class="ni ni-check-bold"></i>
              Устройство подключено к той же сети Wi-Fi
            </li>
            <li>
              <i class="ni ni-check-bold"></i>
              Мобильное приложение провайдера установлено на устройстве
            </li>
            <li>
              <i class="ni ni-check-bold"></i>
              CA-сертификат установлен и доверен на устройстве
            </li>
          </ul>
        </div>

        <div class="certificate-notice">
          <p>Для установки сертификата подключитесь к прокси и откройте <a href="http://mitm.it" target="_blank" rel="noopener">http://mitm.it</a> в браузере устройства.</p>
        </div>
      </div>

      <!-- Step 2: Device Configuration -->
      <div v-if="currentStep === 2" class="step-config">
        <DeviceConfigInstructions :connection="connectionInfo" />
      </div>

      <!-- Step 3: Waiting for Login -->
      <div v-if="currentStep === 3" class="step-waiting">
        <CaptureStatusDisplay
          :start-time="captureStartTime"
          :timeout-minutes="5"
        />
      </div>

      <!-- Step 4: Credentials Preview -->
      <div v-if="currentStep === 4" class="step-preview">
        <CredentialPreview :captured-data="capturedData" />
      </div>

      <!-- Step 5: Completion -->
      <div v-if="currentStep === 5" class="step-complete">
        <div class="complete-icon">
          <i class="ni ni-check-bold"></i>
        </div>
        <h3 class="complete-title">Данные успешно сохранены!</h3>
        <p class="complete-description">
          Учётные данные провайдера были успешно обновлены. Теперь вы можете использовать провайдера для операций.
        </p>
      </div>
    </div>

    <!-- Error Alert -->
    <transition name="fade">
      <div v-if="errorMessage" class="error-alert">
        <i class="ni ni-notification-70"></i>
        {{ errorMessage }}
      </div>
    </transition>

    <!-- Footer Buttons -->
    <template v-slot:modal-footer>
      <div class="modal-footer-content">
        <!-- Step 1 buttons -->
        <template v-if="currentStep === 1">
          <base-button type="secondary" @click="handleClose">
            Отмена
          </base-button>
          <base-button
            v-if="!proxyAlreadyRunning"
            type="primary"
            @click="startCapture"
            :disabled="loading || checkingStatus"
          >
            <span v-if="loading || checkingStatus">
              <i class="ni ni-settings-gear-65 spin mr-2"></i>
              {{ checkingStatus ? 'Проверка...' : 'Запуск...' }}
            </span>
            <span v-else>
              <i class="ni ni-button-play mr-2"></i>
              Начать захват
            </span>
          </base-button>
        </template>

        <!-- Step 2 buttons -->
        <template v-if="currentStep === 2">
          <base-button type="secondary" @click="stopAndClose">
            Отмена
          </base-button>
          <base-button type="primary" @click="goToWaiting">
            <i class="ni ni-check-bold mr-2"></i>
            Устройство настроено
          </base-button>
        </template>

        <!-- Step 3 buttons -->
        <template v-if="currentStep === 3">
          <base-button type="danger" outline @click="stopAndClose" :disabled="loading">
            <i class="ni ni-fat-remove mr-2"></i>
            Отменить
          </base-button>
        </template>

        <!-- Step 4 buttons -->
        <template v-if="currentStep === 4">
          <base-button type="danger" outline @click="rejectCredentials" :disabled="loading">
            <i class="ni ni-fat-remove mr-2"></i>
            Отклонить
          </base-button>
          <base-button type="success" @click="confirmCredentials" :disabled="loading">
            <span v-if="loading">
              <i class="ni ni-settings-gear-65 spin mr-2"></i>
              Сохранение...
            </span>
            <span v-else>
              <i class="ni ni-check-bold mr-2"></i>
              Подтвердить и сохранить
            </span>
          </base-button>
        </template>

        <!-- Step 5 buttons -->
        <template v-if="currentStep === 5">
          <base-button type="primary" @click="handleClose">
            <i class="ni ni-check-bold mr-2"></i>
            Закрыть
          </base-button>
        </template>
      </div>
    </template>
  </b-modal>
</template>

<script>
import CaptureStepIndicator from '@/components/Proxy/CaptureStepIndicator.vue';
import DeviceConfigInstructions from '@/components/Proxy/DeviceConfigInstructions.vue';
import CaptureStatusDisplay from '@/components/Proxy/CaptureStatusDisplay.vue';
import CredentialPreview from '@/components/Proxy/CredentialPreview.vue';
import captureService from '@/services/captureService';

export default {
  name: 'CredentialCaptureModal',
  components: {
    CaptureStepIndicator,
    DeviceConfigInstructions,
    CaptureStatusDisplay,
    CredentialPreview
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    providerId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      loading: false,
      checkingStatus: false,
      proxyAlreadyRunning: false,
      existingProviderId: null,
      errorMessage: null,
      connectionInfo: {
        host: '',
        port: '',
        cert_url: ''
      },
      // Captured fields from API (masked data, no actual credentials)
      capturedData: {
        phone: '',           // Masked phone e.g. "+998****1234"
        has_password: false, // Boolean - whether password was captured
        has_token: false,    // Boolean - whether token was captured
        device_name: ''      // Device name from login request
      },
      captureStartTime: null,
      statusPollingInterval: null,
      pollingActive: false
    };
  },
  computed: {
    modalTitle() {
      const titles = {
        1: 'Захват данных — Введение',
        2: 'Захват данных — Настройка устройства',
        3: 'Захват данных — Ожидание входа',
        4: 'Захват данных — Проверка данных',
        5: 'Захват данных — Готово'
      };
      return titles[this.currentStep] || 'Захват данных';
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetState();
        this.checkProxyStatus();
      } else {
        this.stopPolling();
      }
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    resetState() {
      this.currentStep = 1;
      this.loading = false;
      this.checkingStatus = false;
      this.proxyAlreadyRunning = false;
      this.existingProviderId = null;
      this.errorMessage = null;
      this.connectionInfo = { host: '', port: '', cert_url: '' };
      this.capturedData = { phone: '', has_password: false, has_token: false, device_name: '' };
      this.captureStartTime = null;
      this.stopPolling();
    },

    async checkProxyStatus() {
      try {
        this.checkingStatus = true;
        const response = await captureService.getProxyStatus();

        if (response.data.status === true && response.data.proxy_running) {
          this.proxyAlreadyRunning = true;
          this.existingProviderId = response.data.provider_id || null;
        } else {
          this.proxyAlreadyRunning = false;
          this.existingProviderId = null;
        }
      } catch (error) {
        console.error('Error checking proxy status:', error);
        // If we can't check status, assume it's not running
        this.proxyAlreadyRunning = false;
      } finally {
        this.checkingStatus = false;
      }
    },

    async stopExistingAndStart() {
      try {
        this.loading = true;
        this.clearError();

        // Stop existing proxy session
        if (this.existingProviderId) {
          await captureService.stopCapture(this.existingProviderId);
        } else {
          // Try to stop using current provider ID as fallback
          await captureService.stopCapture(this.providerId);
        }

        // Clear the warning
        this.proxyAlreadyRunning = false;
        this.existingProviderId = null;

        // Now start new capture
        await this.startCapture();
      } catch (error) {
        console.error('Error stopping existing proxy:', error);
        const errMsg = error.response && error.response.data && error.response.data.message;
        this.showError(errMsg || 'Не удалось остановить существующую сессию');
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      if (this.currentStep === 3 || this.currentStep === 4) {
        // Don't allow close during capture/preview without explicit action
        return;
      }
      this.stopPolling();
      this.$emit('update:show', false);
      this.$emit('close');
    },

    async stopAndClose() {
      try {
        this.loading = true;
        await captureService.stopCapture(this.providerId);
      } catch (error) {
        console.error('Error stopping capture:', error);
      } finally {
        this.loading = false;
        this.stopPolling();
        this.$emit('update:show', false);
        this.$emit('close');
      }
    },

    async startCapture() {
      try {
        this.loading = true;
        this.clearError();

        const response = await captureService.startCapture(this.providerId);

        if (response.data.status === true && response.data.connection) {
          // Parse connection info from new API structure
          const conn = response.data.connection;
          this.connectionInfo = {
            host: conn.host || '',
            port: conn.port || '',
            cert_url: conn.cert_url || ''
          };
          this.proxyAlreadyRunning = false;
          this.currentStep = 2;
        } else if (response.data.already_running || response.data.code === 'PROXY_ALREADY_RUNNING') {
          // Handle already running response
          this.proxyAlreadyRunning = true;
          this.existingProviderId = response.data.provider_id || null;
          this.showError(response.data.message || 'Прокси уже запущен');
        } else {
          this.showError(response.data.message || 'Не удалось запустить захват');
        }
      } catch (error) {
        console.error('Error starting capture:', error);
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else if (error.response && error.response.status === 409) {
          // Conflict - proxy already running
          this.proxyAlreadyRunning = true;
          const data = error.response.data || {};
          this.existingProviderId = data.provider_id || null;
          this.showError(data.message || 'Прокси уже запущен другой сессией');
        } else {
          const errMsg = error.response && error.response.data && error.response.data.message;
          this.showError(errMsg || 'Ошибка при запуске захвата');
        }
      } finally {
        this.loading = false;
      }
    },

    goToWaiting() {
      this.currentStep = 3;
      this.captureStartTime = Date.now();
      this.startPolling();
    },

    startPolling() {
      this.pollingActive = true;
      this.pollStatus();
      this.statusPollingInterval = setInterval(() => {
        if (this.pollingActive) {
          this.pollStatus();
        }
      }, 3000);
    },

    stopPolling() {
      this.pollingActive = false;
      if (this.statusPollingInterval) {
        clearInterval(this.statusPollingInterval);
        this.statusPollingInterval = null;
      }
    },

    async pollStatus() {
      if (!this.pollingActive) return;

      try {
        const response = await captureService.getStatus(this.providerId);

        if (response.data.status === true) {
          const state = response.data.state;

          // Check for captured state - captured_fields contains masked data only
          if (state === 'captured' && response.data.capture && response.data.capture.captured_fields) {
            this.stopPolling();
            const fields = response.data.capture.captured_fields;
            // Store masked/indicator data (no actual credentials exposed)
            this.capturedData = {
              phone: fields.phone || '',           // Masked phone e.g. "+998****1234"
              has_password: fields.has_password || false,  // Boolean
              has_token: fields.has_token || false,        // Boolean
              device_name: fields.device_name || ''        // Device name
            };
            this.currentStep = 4;
          } else if (state === 'error') {
            this.stopPolling();
            this.showError(response.data.message || 'Произошла ошибка при захвате');
          } else if (state === 'timeout') {
            this.stopPolling();
            this.showError('Время ожидания истекло. Попробуйте снова.');
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
        if (error.response && error.response.status === 401) {
          this.stopPolling();
          this.$router.push({ name: 'login' });
        }
      }
    },

    async confirmCredentials() {
      try {
        this.loading = true;
        this.clearError();

        const response = await captureService.confirmCapture(this.providerId);

        if (response.data.status === true) {
          this.currentStep = 5;
          this.$emit('success');
        } else {
          this.showError(response.data.message || 'Не удалось сохранить данные');
        }
      } catch (error) {
        console.error('Error confirming credentials:', error);
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          const errMsg = error.response && error.response.data && error.response.data.message;
          this.showError(errMsg || 'Ошибка при сохранении данных');
        }
      } finally {
        this.loading = false;
      }
    },

    async rejectCredentials() {
      try {
        this.loading = true;
        this.clearError();

        const response = await captureService.rejectCapture(this.providerId);

        if (response.data.status === true) {
          // Go back to waiting state
          this.currentStep = 3;
          this.captureStartTime = Date.now();
          this.startPolling();
        } else {
          this.showError(response.data.message || 'Не удалось отклонить данные');
        }
      } catch (error) {
        console.error('Error rejecting credentials:', error);
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          const errMsg = error.response && error.response.data && error.response.data.message;
          this.showError(errMsg || 'Ошибка при отклонении данных');
        }
      } finally {
        this.loading = false;
      }
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = null;
      }, 5000);
    },

    clearError() {
      this.errorMessage = null;
    }
  }
};
</script>

<style lang="scss">
.credential-capture-modal {
  .modal-dialog {
    max-width: 650px;
  }

  .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  .modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 1.25rem 1.5rem;

    .modal-title {
      font-weight: 600;
      color: #333;
    }
  }

  .modal-body {
    padding: 0 1.5rem 1.5rem;
  }

  .modal-footer {
    border-top: 1px solid #f0f0f0;
    padding: 1rem 1.5rem;

    .modal-footer-content {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      width: 100%;
    }
  }
}
</style>

<style scoped lang="scss">
.step-content {
  min-height: 300px;
}

// Step 1: Introduction
.step-intro {
  text-align: center;
  padding: 1rem 0;

  .intro-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: rgba(67, 97, 238, 0.1);
    color: #4361EE;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;

    i {
      font-size: 2rem;
    }
  }

  .intro-title {
    font-size: 1.35rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.75rem;
  }

  .intro-description {
    color: #606266;
    font-size: 0.95rem;
    max-width: 450px;
    margin: 0 auto 1.5rem;
    line-height: 1.6;
  }

  .proxy-warning {
    display: flex;
    gap: 1rem;
    text-align: left;
    background-color: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;

    .warning-icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: rgba(255, 193, 7, 0.2);
      color: #d48806;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 1.25rem;
      }
    }

    .warning-content {
      flex: 1;

      h4 {
        font-size: 1rem;
        font-weight: 600;
        color: #d48806;
        margin: 0 0 0.5rem 0;
      }

      p {
        color: #806600;
        font-size: 0.875rem;
        margin: 0 0 1rem 0;
        line-height: 1.5;
      }
    }
  }

  .requirements-section {
    text-align: left;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;

    h4 {
      font-size: 0.95rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.75rem;
    }

    .requirements-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 0;
        color: #606266;
        font-size: 0.9rem;

        i {
          color: #52c41a;
          font-size: 0.85rem;
        }
      }
    }
  }

  .certificate-notice {
    background-color: #f0f7ff;
    border-radius: 6px;
    padding: 1rem;

    p {
      color: #606266;
      font-size: 0.875rem;
      margin: 0;
      line-height: 1.5;
    }

    a {
      color: #4361EE;
      font-weight: 500;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// Step 5: Completion
.step-complete {
  text-align: center;
  padding: 2rem 1rem;

  .complete-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: rgba(82, 196, 26, 0.1);
    color: #52c41a;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    animation: scaleIn 0.3s ease;

    i {
      font-size: 2.5rem;
    }
  }

  .complete-title {
    font-size: 1.35rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.75rem;
  }

  .complete-description {
    color: #606266;
    font-size: 0.95rem;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.6;
  }
}

// Error alert
.error-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: rgba(255, 77, 79, 0.1);
  border-radius: 6px;
  color: #ff4d4f;
  font-size: 0.9rem;
  margin-top: 1rem;

  i {
    font-size: 1.1rem;
  }
}

// Animations
@keyframes scaleIn {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
